import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { usePageSEO } from '../hooks/usePageSEO';
import { buildMarketingPageSchema } from '../utils/pageSchema';
import { MarketingHeader, MarketingFooter } from './OtherPages';
import {
  ArrowRight, Video, Mic, Brain, BarChart2, MessageSquare,
  Sparkles, CheckCircle2, Clock,
  Target, Zap, Star, Play, TrendingUp, AlertCircle,
} from 'lucide-react';

const PAGE_CSS = `
  * { box-sizing: border-box; }
  @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
  @keyframes pulseRed { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.7;transform:scale(0.95)} }
  @keyframes floatUp { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
  .avi-fade { animation: fadeUp 0.55s ease both; }
  /* Fluid so the longer keyword headline never overflows the hero column. */
  .avi-h1 { font-size: clamp(30px, 4vw, 42px); }
  .avi-feature-card { background:white;border:1px solid #E5E7EB;border-radius:16px;padding:28px 24px;transition:all 0.25s; }
  .avi-feature-card:hover { box-shadow:0 12px 32px rgba(0,0,0,0.08);transform:translateY(-3px);border-color:#D1D5DB; }
  .avi-step-num { width:36px;height:36px;border-radius:50%;background:#EF1D28;color:white;font-weight:900;font-size:14px;display:flex;align-items:center;justify-content:center;flex-shrink:0; }
  .avi-faq-item { background:white;border:1.5px solid #E5E7EB;border-radius:16px;overflow:hidden;transition:border-color 0.25s ease, box-shadow 0.25s ease; }
  .avi-faq-item.open { border-color:#FECACA;box-shadow:0 10px 30px rgba(239,29,40,0.08); }
  .avi-faq-icon { position:relative;width:30px;height:30px;border-radius:9px;background:#F9FAFB;border:1px solid #E5E7EB;flex-shrink:0;transition:background 0.25s ease, border-color 0.25s ease; }
  .avi-faq-item.open .avi-faq-icon { background:#FFF1F2;border-color:#FECDD3; }
  .avi-faq-icon .bar { position:absolute;top:50%;left:50%;background:#6B7280;border-radius:2px; }
  .avi-faq-icon .bar.h { width:12px;height:2px;margin:-1px 0 0 -6px; }
  .avi-faq-icon .bar.v { width:2px;height:12px;margin:-6px 0 0 -1px; }
  .avi-faq-item.open .avi-faq-icon .bar { background:#EF1D28; }
  .avi-btn-primary { background:#EF1D28;color:white;border:none;border-radius:10px;padding:14px 32px;font-weight:700;font-size:16px;cursor:pointer;font-family:inherit;display:inline-flex;align-items:center;gap:8px;transition:all 0.2s;box-shadow:0 4px 16px rgba(239,29,40,0.3); }
  .avi-btn-primary:hover { background:#D91620;transform:translateY(-2px);box-shadow:0 8px 24px rgba(239,29,40,0.4); }
  .avi-btn-outline { background:white;color:#374151;border:1.5px solid #E5E7EB;border-radius:10px;padding:13px 28px;font-weight:600;font-size:15px;cursor:pointer;font-family:inherit;display:inline-flex;align-items:center;gap:7px;transition:all 0.2s; }
  .avi-btn-outline:hover { border-color:#D1D5DB;background:#F9FAFB; }
  .avi-metric { background:#0D1117;border-radius:12px;padding:16px 20px;border:1px solid #1E2D3E; }
  @media (max-width:900px) {
    .avi-hero-grid { flex-direction:column!important; }
    .avi-features-grid { grid-template-columns:repeat(2,1fr)!important; }
    .avi-steps-grid { grid-template-columns:1fr!important; }
    .avi-report-grid { flex-direction:column!important; }
  }
  @media (max-width:600px) {
    .avi-features-grid { grid-template-columns:1fr!important; }
    .avi-stat-row { flex-direction:column!important;gap:0!important; }
    .avi-stat-item { border-left:none!important;border-top:1px solid #F3F4F6; }
  }
`;

const FEATURES = [
  {
    Icon: Mic,
    color: '#7C3AED',
    bg: '#F5F3FF',
    title: 'Real-Time Speech Recognition',
    desc: 'Speak naturally — our AI transcribes every word, tracking pace, clarity, and fluency as you answer.',
  },
  {
    Icon: Brain,
    color: '#0891B2',
    bg: '#F0F9FF',
    title: 'Adaptive AI Questions',
    desc: 'Questions evolve based on your answers. If you mention a skill, the AI probes deeper — just like a real interviewer.',
  },
  {
    Icon: AlertCircle,
    color: '#D97706',
    bg: '#FFFBEB',
    title: 'Filler Word Tracking',
    desc: 'Counts "um", "uh", "like", and other fillers in real time — helping you speak with more confidence and polish.',
  },
  {
    Icon: BarChart2,
    color: '#EF1D28',
    bg: '#FFF1F2',
    title: 'Per-Question Scoring',
    desc: 'Every answer is scored on content depth, relevance, and delivery. See exactly where you shine and where to improve.',
  },
  {
    Icon: TrendingUp,
    color: '#16A34A',
    bg: '#F0FDF4',
    title: 'Hiring Likelihood Score',
    desc: 'Get an overall score indicating how likely you are to pass the interview — with actionable coaching to close the gap.',
  },
  {
    Icon: MessageSquare,
    color: '#DB2777',
    bg: '#FDF2F8',
    title: 'Strengths & Weaknesses Report',
    desc: 'A detailed written report covering your strongest moments and areas to work on before the real interview.',
  },
];

const STEPS = [
  {
    n: '1',
    Icon: Target,
    color: '#EF1D28',
    title: 'Choose Your Role',
    desc: 'Enter your job title (e.g. "Software Engineer" or "Marketing Manager") and the AI tailors every question to your field.',
  },
  {
    n: '2',
    Icon: Video,
    color: '#7C3AED',
    title: 'Answer On Camera',
    desc: 'Your webcam and mic turn on. The AI asks 5–7 role-specific questions — just talk like you would in a real interview.',
  },
  {
    n: '3',
    Icon: Brain,
    color: '#0891B2',
    title: 'AI Follows Up',
    desc: 'After each answer, the AI may ask a follow-up question to explore your skills deeper — up to 10 total.',
  },
  {
    n: '4',
    Icon: BarChart2,
    color: '#16A34A',
    title: 'Get Your Report',
    desc: 'Instantly receive a full performance report: scores, filler word count, strengths, weaknesses, and hiring likelihood.',
  },
];

const FAQS = [
  { q: 'Do I need a webcam to use AI Video Interview?', a: 'You need a microphone at minimum. A webcam is optional — the AI uses your speech to evaluate your answers, so you can practice without video if you prefer.' },
  { q: 'What types of roles are supported?', a: 'Any professional role works — software engineering, marketing, finance, healthcare, design, and more. The AI customizes questions based on the job title you enter.' },
  { q: 'How many questions will I be asked?', a: 'The session includes 5 main questions. The AI may add up to 5 follow-up questions based on your answers, for a maximum of 10 questions per session.' },
  { q: 'How is the hiring likelihood score calculated?', a: "It's a weighted average of your per-question scores, factoring in answer depth, relevance to the role, confidence, and delivery quality. Scores range from 0–100." },
  { q: 'Can I practice multiple times?', a: 'Yes — each session generates a fresh set of questions. You can practice as many times as you like to build confidence before your real interview.' },
  { q: 'Is my session data private?', a: 'Yes. Your audio and transcript are processed to generate your report and are never shared or used to train models without your consent.' },
];

// Kept to ~150 chars so Google shows it whole in the results snippet.
const SEO_DESCRIPTION =
  'Free AI interview practice. Answer real role-specific questions from an AI interview coach, get instant feedback on your delivery, and see a full performance report.';

const REPORT_METRICS = [
  { label: 'Overall Score', value: '84/100', color: '#16A34A', sub: 'Strong candidate' },
  { label: 'Hiring Likelihood', value: '78%', color: '#0891B2', sub: 'Likely to pass' },
  { label: 'Filler Words', value: '12', color: '#D97706', sub: 'Target: under 8' },
  { label: 'Avg Response', value: '92 sec', color: '#7C3AED', sub: 'Ideal: 60–120s' },
];

export const AIVideoInterviewPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openFaq, setOpenFaq] = useState(null);

  const schema = useMemo(() => buildMarketingPageSchema({
    path: '/ai-video-interview',
    name: 'AI Interview Practice – Your Personal Job Interview Coach',
    breadcrumbName: 'AI Interview Practice',
    description: SEO_DESCRIPTION,
    faqs: FAQS.map(({ q, a }) => ({ question: q, answer: a })),
  }), []);

  usePageSEO({
    title: 'AI Interview Practice – Free AI Mock Interview | MarsResume',
    description: SEO_DESCRIPTION,
    canonical: '/ai-video-interview',
    keywords: 'AI interview practice, AI mock interview, interview practice online, AI interview coach, practice job interview, mock interview with AI, interview question practice, AI interview simulator',
    jsonLd: schema,
  });

  const goTo = (path) => navigate(user ? (path === '/signup' ? '/dashboard' : path) : path);
  const startPractice = () => navigate(user ? '/video-interview' : '/signup');

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", background: '#FFFFFF', minHeight: '100vh' }}>
      <style>{PAGE_CSS}</style>
      <MarketingHeader />

      {/* ── Hero ── */}
      <section style={{ background: 'linear-gradient(160deg, #070E1A 0%, #0D1120 55%, #0A1428 100%)', padding: '96px 48px 80px', position: 'relative', overflow: 'hidden' }}>
        {/* Grid dots */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,0.035) 1px, transparent 1px)', backgroundSize: '28px 28px', pointerEvents: 'none' }} />
        {/* Red glow */}
        <div style={{ position: 'absolute', top: '-10%', right: '10%', width: 640, height: 500, background: 'radial-gradient(ellipse, rgba(239,29,40,0.12) 0%, transparent 65%)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', display: 'flex', alignItems: 'center', gap: 72, flexWrap: 'wrap' }} className="avi-hero-grid">
          {/* Left copy */}
          <div className="avi-fade" style={{ flex: '0 0 50%', minWidth: 300 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(239,29,40,0.15)', border: '1px solid rgba(239,29,40,0.28)', borderRadius: 24, padding: '5px 14px', marginBottom: 24 }}>
              <Video size={12} color="#FF6B6B" />
              <span style={{ fontSize: 11, fontWeight: 700, color: '#FF6B6B', letterSpacing: 0.5 }}>AI-POWERED · FREE TO TRY</span>
            </div>
            <h1 className="avi-h1" style={{ color: '#FFFFFF', fontWeight: 900, letterSpacing: '-1.5px', lineHeight: 1.08, margin: '0 0 20px' }}>
              AI Interview Practice<br />
              <span style={{ color: '#EF1D28' }}>Your Personal Job Interview Coach</span>
            </h1>
            <p style={{ color: '#94A3B8', fontSize: 18, lineHeight: 1.75, margin: '0 0 36px', maxWidth: 460 }}>
              Practice interviews with an AI coach that asks real role-specific questions, tracks your
              speech and filler words, and delivers a full performance report — so you walk into the
              real interview ready.
            </p>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
              <button className="avi-btn-primary" onClick={startPractice}>
                <Play size={16} fill="white" /> Start practicing free
              </button>
              <div style={{ display: 'flex', gap: 20 }}>
                {[['5–10', 'min session'], ['AI', 'feedback'], ['Free', 'to start']].map(([v, l]) => (
                  <div key={l} style={{ textAlign: 'center' }}>
                    <div style={{ color: 'white', fontWeight: 900, fontSize: 18, letterSpacing: '-0.5px' }}>{v}</div>
                    <div style={{ color: '#64748B', fontSize: 11, fontWeight: 500 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right — mock interview card */}
          <div className="avi-fade" style={{ flex: 1, minWidth: 300, animationDelay: '0.1s' }}>
            <div style={{ background: '#0F1929', border: '1px solid #1E2D45', borderRadius: 20, padding: 28, boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}>
              {/* Header bar */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#EF1D28', animation: 'pulseRed 1.8s ease-in-out infinite' }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#EF1D28' }}>LIVE SESSION</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '4px 10px' }}>
                  <Clock size={11} color="#64748B" />
                  <span style={{ fontSize: 11, color: '#64748B', fontWeight: 600 }}>02:14</span>
                </div>
              </div>

              {/* Video placeholder */}
              <div style={{ background: '#161D2C', borderRadius: 14, height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20, position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 50% 80%, rgba(239,29,40,0.08), transparent 70%)' }} />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg,#7C3AED,#4F46E5)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
                    <span style={{ color: 'white', fontWeight: 900, fontSize: 20 }}>JD</span>
                  </div>
                  <div style={{ color: '#94A3B8', fontSize: 12, fontWeight: 500 }}>John Doe · Software Engineer</div>
                </div>
                {/* Mic indicator */}
                <div style={{ position: 'absolute', bottom: 12, right: 12, width: 28, height: 28, borderRadius: '50%', background: '#EF1D28', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Mic size={12} color="white" />
                </div>
              </div>

              {/* Current question */}
              <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#64748B', letterSpacing: 0.5, marginBottom: 6 }}>QUESTION 3 OF 7</div>
                <div style={{ color: '#E2E8F0', fontSize: 13, lineHeight: 1.6, fontWeight: 500 }}>
                  "Tell me about a time you handled a difficult technical challenge under a tight deadline."
                </div>
              </div>

              {/* Metrics row */}
              <div style={{ display: 'flex', gap: 8 }}>
                {[['Score', '88'], ['Filler words', '3'], ['Pace', 'Good']].map(([l, v]) => (
                  <div key={l} style={{ flex: 1, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ color: '#94A3B8', fontSize: 9, fontWeight: 600, marginBottom: 3 }}>{l.toUpperCase()}</div>
                    <div style={{ color: 'white', fontWeight: 800, fontSize: 14 }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stat bar ── */}
      <section style={{ background: 'white', borderBottom: '1px solid #F3F4F6', padding: '40px 48px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', gap: 0 }} className="avi-stat-row">
          {[
            { v: '5–7', l: 'Role-specific questions', s: 'per session' },
            { v: '10', l: 'Max adaptive questions', s: 'AI follows up on answers' },
            { v: '100', l: 'Point scoring scale', s: 'per question + overall' },
            { v: '<30s', l: 'Report generated', s: 'instantly after session' },
          ].map(({ v, l, s }, i) => (
            <div key={i} className="avi-stat-item" style={{ flex: 1, textAlign: 'center', padding: '16px 24px', borderLeft: i > 0 ? '1px solid #F3F4F6' : 'none' }}>
              <div style={{ color: '#0D1117', fontWeight: 900, fontSize: 36, letterSpacing: '-1.5px', lineHeight: 1, marginBottom: 6 }}>{v}</div>
              <div style={{ color: '#374151', fontSize: 13, fontWeight: 700, marginBottom: 3 }}>{l}</div>
              <div style={{ color: '#9CA3AF', fontSize: 11 }}>{s}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── How It Works ── */}
      <section style={{ padding: '96px 48px', background: '#F8FAFC' }}>
        <div style={{ maxWidth: 1060, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: '#FFF1F2', border: '1px solid #FECACA', borderRadius: 24, padding: '5px 14px', marginBottom: 14 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#DC2626', letterSpacing: 0.5 }}>✦ HOW IT WORKS</span>
            </div>
            <h2 style={{ margin: '0 0 14px', fontSize: 40, fontWeight: 900, color: '#0D1117', letterSpacing: '-1px', lineHeight: 1.1 }}>
              From setup to report in minutes
            </h2>
            <p style={{ margin: '0 auto', color: '#6B7280', fontSize: 17, maxWidth: 440, lineHeight: 1.7 }}>
              No setup hassle — just pick your role and start talking.
            </p>
          </div>

          <div className="avi-steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
            {STEPS.map(({ n, Icon, color, title, desc }, i) => (
              <div key={i} style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 16, padding: '28px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                  <div className="avi-step-num">{n}</div>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={18} color={color} strokeWidth={2} />
                  </div>
                </div>
                <div style={{ fontWeight: 800, fontSize: 15, color: '#111827', marginBottom: 10, lineHeight: 1.3 }}>{title}</div>
                <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.72 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section style={{ padding: '96px 48px', background: 'white' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: '#FFF1F2', border: '1px solid #FECACA', borderRadius: 24, padding: '5px 14px', marginBottom: 14 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#DC2626', letterSpacing: 0.5 }}>✦ FEATURES</span>
            </div>
            <h2 style={{ margin: '0 0 14px', fontSize: 40, fontWeight: 900, color: '#0D1117', letterSpacing: '-1px', lineHeight: 1.1 }}>
              Everything a real interviewer notices
            </h2>
            <p style={{ margin: '0 auto', color: '#6B7280', fontSize: 17, maxWidth: 480, lineHeight: 1.7 }}>
              MarsResume's AI evaluates you on the same dimensions real hiring managers care about.
            </p>
          </div>

          <div className="avi-features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 18 }}>
            {FEATURES.map(({ Icon, color, bg, title, desc }, i) => (
              <div key={i} className="avi-feature-card">
                <div style={{ width: 48, height: 48, borderRadius: 14, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
                  <Icon size={22} color={color} strokeWidth={2} />
                </div>
                <div style={{ fontWeight: 800, fontSize: 16, color: '#111827', marginBottom: 10, lineHeight: 1.3 }}>{title}</div>
                <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.72 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Sample Report ── */}
      <section style={{ padding: '96px 48px', background: 'linear-gradient(160deg, #070E1A 0%, #0D1120 100%)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '28px 28px', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 700, height: 400, background: 'radial-gradient(ellipse, rgba(239,29,40,0.08) 0%, transparent 65%)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: 1000, margin: '0 auto', position: 'relative' }}>
          <div style={{ textAlign: 'center', marginBottom: 60 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(239,29,40,0.14)', border: '1px solid rgba(239,29,40,0.24)', borderRadius: 24, padding: '5px 14px', marginBottom: 14 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#FF6B6B', letterSpacing: 0.5 }}>✦ SAMPLE REPORT</span>
            </div>
            <h2 style={{ margin: '0 0 14px', fontSize: 40, fontWeight: 900, color: 'white', letterSpacing: '-1px', lineHeight: 1.1 }}>
              What your report looks like
            </h2>
            <p style={{ margin: '0 auto', color: '#64748B', fontSize: 17, maxWidth: 440, lineHeight: 1.7 }}>
              Immediately after your session, you get a full breakdown.
            </p>
          </div>

          <div className="avi-report-grid" style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* Metric cards */}
            <div style={{ flex: '0 0 280px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {REPORT_METRICS.map(({ label, value, color, sub }) => (
                <div key={label} className="avi-metric">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#475569', letterSpacing: 0.3 }}>{label.toUpperCase()}</span>
                    <span style={{ fontSize: 22, fontWeight: 900, color, letterSpacing: '-0.5px' }}>{value}</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#374151' }}>{sub}</div>
                </div>
              ))}
            </div>

            {/* Strength/Weakness sample */}
            <div style={{ flex: 1, minWidth: 280 }}>
              <div style={{ background: '#0F1929', border: '1px solid #1E2D45', borderRadius: 16, padding: 28, marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#16A34A', marginBottom: 14, letterSpacing: 0.3 }}>✓ STRENGTHS</div>
                {[
                  'Clear STAR structure in behavioural answers',
                  'Strong technical vocabulary for the role',
                  'Confident and measured speaking pace',
                ].map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                    <CheckCircle2 size={14} color="#16A34A" style={{ flexShrink: 0, marginTop: 1 }} />
                    <span style={{ color: '#94A3B8', fontSize: 13, lineHeight: 1.6 }}>{s}</span>
                  </div>
                ))}
              </div>
              <div style={{ background: '#0F1929', border: '1px solid #1E2D45', borderRadius: 16, padding: 28 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#D97706', marginBottom: 14, letterSpacing: 0.3 }}>↗ AREAS TO IMPROVE</div>
                {[
                  'Reduce filler words ("um", "uh") — counted 12 times',
                  'Quantify impact in 2 of your 7 answers',
                  'Keep answers between 60–120 seconds for best scoring',
                ].map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                    <AlertCircle size={14} color="#D97706" style={{ flexShrink: 0, marginTop: 1 }} />
                    <span style={{ color: '#94A3B8', fontSize: 13, lineHeight: 1.6 }}>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Testimonials strip ── */}
      <section style={{ padding: '72px 48px', background: '#F8FAFC' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center', marginBottom: 10 }}>
            {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="#F59E0B" color="#F59E0B" />)}
            <span style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginLeft: 6 }}>4.9 / 5 from early users</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginTop: 32 }} className="avi-features-grid">
            {[
              { text: 'I bombed my first mock, but after 3 sessions I got the offer. The filler word tracking alone changed how I speak.', name: 'Priya M.', role: 'Software Engineer at Stripe' },
              { text: 'The adaptive questions are scary realistic. It asked me a follow-up on my Kubernetes answer that I was NOT ready for — exactly what happened in the real interview.', name: 'James K.', role: 'DevOps Engineer' },
              { text: 'Got a 91/100 on my 4th practice. Walked into the actual interview so calm. Got the offer the next day.', name: 'Sara L.', role: 'Product Manager' },
            ].map(({ text, name, role }, i) => (
              <div key={i} style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 16, padding: '24px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', gap: 2, marginBottom: 14 }}>
                  {[...Array(5)].map((_, j) => <Star key={j} size={13} fill="#F59E0B" color="#F59E0B" />)}
                </div>
                <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.75, margin: '0 0 18px', fontStyle: 'italic' }}>&ldquo;{text}&rdquo;</p>
                <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{name}</div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={{ padding: '96px 48px', background: 'white' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <h2 style={{ margin: '0 0 14px', fontSize: 38, fontWeight: 900, color: '#0D1117', letterSpacing: '-0.8px' }}>
              Common questions
            </h2>
            <p style={{ margin: '0 auto', color: '#6B7280', fontSize: 16, maxWidth: 440, lineHeight: 1.7 }}>
              Everything you need to know before your first AI practice session.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {FAQS.map(({ q, a }, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} className={`avi-faq-item${isOpen ? ' open' : ''}`}>
                  <button type="button" aria-expanded={isOpen} onClick={() => setOpenFaq(isOpen ? null : i)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', gap: 16, fontFamily: 'inherit' }}>
                    <span style={{ fontSize: 15.5, fontWeight: 700, color: '#111827', lineHeight: 1.4 }}>{q}</span>
                    <span className="avi-faq-icon">
                      <span className="bar h" />
                      <motion.span className="bar v" animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.25, ease: 'easeInOut' }} />
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: 'hidden' }}>
                        <div style={{ padding: '0 24px 22px', fontSize: 14.5, color: '#6B7280', lineHeight: 1.8 }}>{a}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section style={{ padding: '80px 48px', background: '#F8FAFC' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <div style={{ background: 'linear-gradient(150deg, #060D1A 0%, #0D1120 100%)', borderRadius: 24, padding: '60px 48px', textAlign: 'center', position: 'relative', overflow: 'hidden', border: '1px solid #1A2A3E' }}>
            <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '24px 24px', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', top: -80, left: '50%', transform: 'translateX(-50%)', width: 500, height: 300, background: 'radial-gradient(ellipse, rgba(239,29,40,0.12) 0%, transparent 65%)', pointerEvents: 'none' }} />
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(239,29,40,0.15)', border: '1px solid rgba(239,29,40,0.28)', borderRadius: 24, padding: '5px 14px', marginBottom: 20 }}>
                <Sparkles size={12} color="#FF6B6B" />
                <span style={{ fontSize: 11, fontWeight: 700, color: '#FF6B6B', letterSpacing: 0.5 }}>NO CREDIT CARD REQUIRED</span>
              </div>
              <h2 style={{ color: 'white', fontSize: 38, fontWeight: 900, letterSpacing: '-1px', lineHeight: 1.1, margin: '0 0 16px' }}>
                Your next interview starts here.
              </h2>
              <p style={{ color: '#64748B', fontSize: 16, lineHeight: 1.75, margin: '0 0 36px', maxWidth: 440, marginLeft: 'auto', marginRight: 'auto' }}>
                Practice on camera. Get real feedback. Walk in confident.
              </p>
              <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button className="avi-btn-primary" onClick={startPractice} style={{ fontSize: 16, padding: '15px 36px' }}>
                  <Play size={17} fill="white" /> Start practicing free
                </button>
                <button className="avi-btn-outline" onClick={() => navigate('/pricing')} style={{ padding: '14px 28px', background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.12)', color: '#CBD5E1' }}>
                  View pricing <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter navigate={navigate} goTo={goTo} />
    </div>
  );
};

export default AIVideoInterviewPage;
